//* Libraries imports
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeftIcon,
  SaveIcon,
  Loader2Icon,
  UserIcon,
  PackageIcon,
  UserCheckIcon,
  SettingsIcon,
  DollarSignIcon,
  AlertCircleIcon,
  CreditCardIcon,
  FileTextIcon,
} from "lucide-react";

//* Hooks Imports
import { type AppDispatch, type RootState } from "@/app/store";
import { fetchClients } from "@features/Client/ClientSlice";
import { fetchProducts } from "@features/Element/productSlice";
import { fetchUsers } from "@features/user/UserSlice";
import { useGetSale } from "@hooks/sales/queries/useGetSale";
import { useCreateSale } from "@hooks/sales/mutations/useCreateSale";
import { useUpdateSale } from "@hooks/sales/mutations/useUpdateSale";

//* Types Imports
import { type ClientOutputDTO } from "@features/Client/types";
import { type IProduct } from "@features/Element/types";
import { type UserOutputDTO } from "@features/user/types";
import {
  type ISaleInputDTO,
  SaleType,
  type SaleFormData,
  saleSchemaForm,
} from "@/schemas/sales";
import { PaymentStatusSchema } from "@/schemas/payment";

//* UI Components
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { NumericInput } from "@/components/ui/numeric-input";

//* Section Components
import { FinancialConfigForm } from "@features/Element/sections/FinancialConfigForm";
import { MediaUpload } from "../Element/sections/components/MediaUpload";
import { API_URL, getAuthHeaders } from "@/config/api";
import axios from "axios";

function getEntityId(entity: any): string {
  if (!entity) return "";
  if (typeof entity === "string") return entity;
  return entity._id || entity.id || "";
}

export function SalesForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isEditing = !!id;

  const queryGetSale = useGetSale(id);
  const mutationCreateSale = useCreateSale();
  const mutationUpdateSale = useUpdateSale();

  const [linkContract,setLinkContract] = useState(null);
  const [loadingFile,setLoadingFile] = useState(false);


  const { items: clients, loading: clientsLoading } = useSelector(
    (state: RootState) => state.client,
  );
  const { products, loading: productsLoading } = useSelector(
    (state: RootState) => state.product,
  );
  const { items: users, loading: usersLoading } = useSelector(
    (state: RootState) => state.user,
  );
  const { user: currentUser } = useSelector((state: RootState) => state.auth);


  const saveFile = async (file: any) => {
    setLoadingFile(true);
      let headers = getAuthHeaders();
      delete headers["Content-Type"];
      // headers['Content-Type'] = 'multipart/form-data';
      let form_data = new FormData();
      form_data.append("file", file);
      form_data.append("mime", file.type.split("/")[1]);
  
      let response = await axios.post(API_URL + "/products/file", form_data, {
        headers,
      });
      console.log(response);
    setLoadingFile(false);

      return response;
    };

  const clientList: ClientOutputDTO[] = Array.isArray(clients)
    ? clients
    : (clients as any)?.data || [];
  const productList: IProduct[] = Array.isArray(products)
    ? products
    : (products as any)?.data || [];
  const userList: UserOutputDTO[] = Array.isArray(users)
    ? users
    : (users as any)?.data || [];

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SaleFormData>({
    resolver: zodResolver(saleSchemaForm),
    defaultValues: {
      clientId: "",
      productId: "",
      responsibleId: "",
      financialConfig: {
        cashPrice: undefined,
        installmentPrice: undefined,
        paymentMethods: [],
        maxInstallments: undefined,
        interestFreeInstallments: undefined,
        interestRate: undefined,
        standardCashDiscount: undefined,
        standardInstallmentDiscount: undefined,
        managerOverrideDiscount: undefined,
      },
      saleType: SaleType.COMPLETE_PRODUCT,
      paymentStatus: PaymentStatusSchema.Enum.CREATED,
      saleDate: null,
      managerDiscount: undefined,
    },
  });

  const [overridePrice, setOverridePrice] = useState<boolean>(false);
  const [useManagerDiscount, setUseManagerDiscount] = useState<boolean>(false);

  const selectedProductId = watch("productId");
  const selectedProduct = productList.find(
    (p) => getEntityId(p) === selectedProductId,
  );

  useEffect(() => {
    if (!isEditing && currentUser) {
      const userId = getEntityId(currentUser);
      if (userId) {
        setValue("responsibleId", userId);
      }
    }
  }, [isEditing, currentUser, setValue]);

  useEffect(() => {
    dispatch(fetchClients({}));
    dispatch(fetchProducts({}));
    dispatch(fetchUsers({}));
  }, [dispatch]);

  useEffect(() => {
    if (queryGetSale.data) {
      reset({
        clientId: getEntityId(queryGetSale.data.clientId),
        productId: getEntityId(queryGetSale.data.productId),
        responsibleId: getEntityId(queryGetSale.data.responsibleId),
        saleType: queryGetSale.data.saleType as any,
        paymentStatus: queryGetSale.data.paymentStatus,
        saleDate: queryGetSale.data.saleDate
          ? new Date(queryGetSale.data.saleDate)
          : null,
        financialConfig: queryGetSale.data.financialConfig || {
          cashPrice: undefined,
          installmentPrice: undefined,
          paymentMethods: [],
          maxInstallments: undefined,
          interestFreeInstallments: undefined,
          interestRate: undefined,
          standardCashDiscount: undefined,
          standardInstallmentDiscount: undefined,
          managerOverrideDiscount: undefined,
        },
      });
      if (
        queryGetSale.data.financialConfig &&
        (queryGetSale.data.financialConfig.cashPrice ||
          queryGetSale.data.financialConfig.installmentPrice)
      ) {
        setOverridePrice(true);
      }
      if (queryGetSale.data.managerDiscount) {
        setUseManagerDiscount(true);
        setValue("managerDiscount", queryGetSale.data.managerDiscount);
      }
    }
  }, [queryGetSale.data, reset]);

  const onSubmit = (data: SaleFormData) => {
    // Fallback de segurança: se responsibleId estiver vazio, tenta usar o currentUser
    const responsibleId =
      getEntityId(data.responsibleId) ||
      (currentUser ? getEntityId(currentUser) : "");

    const payload: ISaleInputDTO = {
      clientId: getEntityId(data.clientId),
      productId: getEntityId(data.productId),
      responsibleId: responsibleId,
      saleType: data.saleType,
      paymentStatus: data.paymentStatus as any,
      financialConfig: data.financialConfig,
      saleDate: data.saleDate ? data.saleDate.toISOString() : undefined,
      managerDiscount: useManagerDiscount ? data.managerDiscount : undefined,
    };

    if (!overridePrice) {
      delete payload.financialConfig;
    } else if (payload.financialConfig) {
      const cleanedConfig = Object.fromEntries(
        Object.entries(payload.financialConfig).filter(([_, v]) => {
          if (v === undefined || v === null) return false;
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === "string") return v !== "";
          return true; // Mantém números e outros tipos
        }),
      );

      if (Object.keys(cleanedConfig).length === 0) {
        delete payload.financialConfig;
      } else {
        payload.financialConfig = cleanedConfig as any;
      }
    }

    if (isEditing && id) {
      mutationUpdateSale.mutate(
        { id, data: payload },
        {
          onSuccess: () => navigate("/sales"),
        },
      );
    } else {
      mutationCreateSale.mutate(payload, {
        onSuccess: () => navigate("/sales"),
      });
    }
  };
  if (
    clientsLoading ||
    productsLoading ||
    queryGetSale.isLoading ||
    usersLoading
  ) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2Icon className="animate-spin" size={48} />
      </div>
    );
  }

  const managerMaxDiscount =
    selectedProduct?.financialConfig?.managerOverrideDiscount || 0;

  return (
    <div className="flex flex-col p-6 w-full max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/sales")}
            className="rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeftIcon size={22} />
          </Button>
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              {isEditing ? "Editar Venda" : "Nova Venda"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEditing
                ? "Atualize as informações da venda selecionada."
                : "Preencha os dados abaixo para registrar uma nova venda."}
            </p>
          </div>
        </div>

        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={
            mutationCreateSale.isPending || mutationUpdateSale.isPending
          }
          className="bg-blue-600 hover:bg-blue-700 shadow-md transition-all gap-2 px-6"
        >
          {mutationCreateSale.isPending || mutationUpdateSale.isPending ? (
            <Loader2Icon className="animate-spin" size={18} />
          ) : (
            <SaveIcon size={18} />
          )}
          <span>{isEditing ? "Atualizar Registro" : "Gerar Venda"}</span>
        </Button>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-none shadow-sm ring-1 ring-gray-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileTextIcon className="text-blue-500" size={20} />
                Informações Principais
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <UserIcon size={14} className="text-gray-400" />
                  Cliente
                </Label>
                <Controller
                  name="clientId"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        id="sales-form-client-select"
                        aria-label="Selecionar cliente"
                        className={`bg-gray-50/50 ${errors.clientId ? "border-red-500 ring-red-50" : "border-gray-200"}`}
                      >
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientList.map((client: any) => {
                          const id = getEntityId(client);
                          return (
                            <SelectItem key={id} value={id}>
                              {client.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.clientId && (
                  <p className="text-[11px] font-medium text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircleIcon size={12} />
                    {errors.clientId.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <PackageIcon size={14} className="text-gray-400" />
                  Produto
                </Label>
                <Controller
                  name="productId"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        id="sales-form-product-select"
                        aria-label="Selecionar produto"
                        className={`bg-gray-50/50 ${errors.productId ? "border-red-500 ring-red-50" : "border-gray-200"}`}
                      >
                        <SelectValue placeholder="Selecione um produto" />
                      </SelectTrigger>
                      <SelectContent>
                        {productList.map((product: any) => {
                          const id = getEntityId(product);
                          return (
                            <SelectItem key={id} value={id}>
                              {product.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.productId && (
                  <p className="text-[11px] font-medium text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircleIcon size={12} />
                    {errors.productId.message}
                  </p>
                )}
              </div>

              {/* <MediaUpload
                          id={id}
                          type="pdf"
                          hidden_name
                          data={{
                            link: linkContract || "",
                            // name: tutorial.pdf?.name || "",
                          }}
                          onUpdate={(field, value:any) => {
                            setLinkContract(value);
                          }}
                          onUpload={saveFile}
                          loadingFile={loadingFile}
                        /> */}
            </CardContent>
          </Card>

          {selectedProduct && (
            <>
              <Card
                className={`border-none shadow-sm ring-1 transition-all duration-300 ${overridePrice ? "ring-blue-200 bg-blue-50/10" : "ring-gray-200"}`}
              >
                <CardHeader className="pb-4 border-b border-gray-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <SettingsIcon
                        className={
                          overridePrice ? "text-blue-500" : "text-gray-400"
                        }
                        size={20}
                      />
                      Configuração Financeira
                    </CardTitle>
                    <Button
                      type="button"
                      variant={overridePrice ? "destructive" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (overridePrice) {
                          setValue("financialConfig", {
                            cashPrice: undefined,
                            installmentPrice: undefined,
                            paymentMethods: [],
                            maxInstallments: undefined,
                            interestFreeInstallments: undefined,
                            interestRate: undefined,
                            standardCashDiscount: undefined,
                            standardInstallmentDiscount: undefined,
                            managerOverrideDiscount: undefined,
                          });
                        } else if (selectedProduct?.financialConfig) {
                          setValue(
                            "financialConfig",
                            selectedProduct.financialConfig,
                          );
                        }
                        setOverridePrice(!overridePrice);
                      }}
                      className="gap-2 h-8 text-xs"
                    >
                      {overridePrice ? (
                        <>
                          <AlertCircleIcon size={14} />
                          Restaurar Padrão
                        </>
                      ) : (
                        <>
                          <DollarSignIcon size={14} />
                          Personalizar Venda
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {overridePrice
                      ? "As configurações abaixo são exclusivas para esta transação."
                      : "Utilizando as políticas financeiras padrão do produto selecionado."}
                  </p>
                </CardHeader>
                <CardContent className="pt-6">
                  {overridePrice ? (
                    <Controller
                      name="financialConfig"
                      control={control}
                      render={({ field }) => (
                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                          <FinancialConfigForm
                            config={field.value as any}
                            onChange={field.onChange}
                            title="Especificação da Venda"
                            onlyPaymentMethods={true}
                          />
                        </div>
                      )}
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          Preço à Vista (Padrão)
                        </span>
                        <span className="text-xl font-bold text-gray-900">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(
                            selectedProduct?.financialConfig?.cashPrice || 0,
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          Preço Parcelado (Padrão)
                        </span>
                        <span className="text-xl font-bold text-gray-900">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(
                            selectedProduct?.financialConfig
                              ?.installmentPrice || 0,
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card
                className={`border-none shadow-sm ring-1 transition-all duration-300 ${useManagerDiscount ? "ring-purple-200 bg-purple-50/10" : "ring-gray-200"}`}
              >
                <CardHeader className="pb-4 border-b border-gray-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSignIcon
                        className={
                          useManagerDiscount
                            ? "text-purple-500"
                            : "text-gray-400"
                        }
                        size={20}
                      />
                      Desconto Gerencial
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor="use-manager-discount"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Aplicar Desconto
                      </Label>
                      <Checkbox
                        id="use-manager-discount"
                        checked={useManagerDiscount}
                        onCheckedChange={(checked) => {
                          setUseManagerDiscount(!!checked);
                          if (!checked) {
                            setValue("managerDiscount", undefined);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {useManagerDiscount
                      ? "Este desconto substituirá os descontos padrão configurados."
                      : "Habilite para aplicar um desconto personalizado nesta venda."}
                  </p>
                </CardHeader>
                {useManagerDiscount && (
                  <CardContent className="pt-6">
                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex flex-col gap-2">
                        <Label className="text-sm font-medium">
                          Porcentagem de Desconto (%)
                        </Label>
                        <div className="flex items-center gap-2">
                          <Controller
                            name="managerDiscount"
                            control={control}
                            render={({ field }) => (
                              <NumericInput
                                id="sales-form-manager-discount-input"
                                aria-label="Porcentagem de desconto gerencial"
                                value={field.value}
                                onChange={(val) => {
                                  if (
                                    val !== undefined &&
                                    managerMaxDiscount > 0
                                  ) {
                                    if (val > managerMaxDiscount) {
                                      field.onChange(managerMaxDiscount);
                                      return;
                                    }
                                  }
                                  field.onChange(val);
                                }}
                                max={managerMaxDiscount}
                                placeholder={`Máximo: ${managerMaxDiscount}%`}
                                className={
                                  errors.managerDiscount ? "border-red-500" : ""
                                }
                              />
                            )}
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                        {managerMaxDiscount > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Limite permitido pelo produto:{" "}
                            <strong>{managerMaxDiscount}%</strong>
                          </p>
                        )}
                        {errors.managerDiscount && (
                          <p className="text-[11px] font-medium text-red-500 flex items-center gap-1 mt-1">
                            <AlertCircleIcon size={12} />
                            {errors.managerDiscount.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            </>
          )}
        </div>

        {/* Sidebar Column */}
        <div className="space-y-6">
          <Card className="border-none shadow-sm ring-1 ring-gray-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCheckIcon className="text-amber-500" size={20} />
                Responsabilidade
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Responsável</Label>
                <Controller
                  name="responsibleId"
                  control={control}
                  render={({ field }) => {
                    const responsibleName =
                      userList.find((u) => getEntityId(u) === field.value)
                        ?.name ||
                      currentUser?.name ||
                      "Carregando...";
                    return (
                      <div className="relative">
                        <UserCheckIcon
                          className="absolute left-3 top-2.5 text-gray-400"
                          size={16}
                        />
                        <input
                          id="sales-form-responsible-input"
                          aria-label="Nome do responsável pela venda"
                          className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 pl-10 text-sm disabled:opacity-90 text-gray-600 font-medium cursor-not-allowed"
                          disabled
                          value={responsibleName}
                          readOnly
                        />
                      </div>
                    );
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Tipo de Venda</Label>
                <Controller
                  name="saleType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={true}
                    >
                      <SelectTrigger
                        id="sales-form-type-select"
                        aria-label="Tipo de venda"
                        className="bg-gray-100 border-gray-200 cursor-not-allowed opacity-70"
                      >
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SaleType.COMPLETE_PRODUCT}>
                          Produto Completo
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {selectedProduct && (
            <Card className="bg-blue-600 border-none shadow-lg text-white">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CreditCardIcon size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-blue-100">Resumo do Produto</p>
                    <p className="font-bold truncate max-w-[150px]">
                      {selectedProduct.name}
                    </p>
                  </div>
                </div>
                <div className="h-px bg-white/10" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] text-blue-200 uppercase font-bold">
                      À Vista
                    </p>
                    <p className="text-sm font-bold">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(
                        overridePrice
                          ? watch("financialConfig.cashPrice") || 0
                          : selectedProduct.financialConfig?.cashPrice || 0,
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-blue-200 uppercase font-bold">
                      Parcelado
                    </p>
                    <p className="text-sm font-bold">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(
                        overridePrice
                          ? watch("financialConfig.installmentPrice") || 0
                          : selectedProduct.financialConfig?.installmentPrice ||
                              0,
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </form>
    </div>
  );
}
